import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';

import {
  ProjectChecklistResponse,
  ProjectChecklistId,
  ProjectChecklistRequest,
  ChecklistType,
  ProjectChecklistStepResponse,
  CompactProjectChecklistResponse,
} from '@business/entities/project';
import { R } from '@core/utils/r';
import { MarketingChecklistAPI } from '@business/api/MarketingChecklist';

export class MarketingChecklistBloc extends BaseBloc {
  private readonly marketingChecklistRepo = new Repository<CompactProjectChecklistResponse | ProjectChecklistResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly checklists$ = (type: ChecklistType) =>
    this.marketingChecklistRepo.items$.pipe(
      map(items => {
        return items.filter(i => i.type === type);
      }),
      map(items => {
        return R.sortBy(items, i => i.name);
      }),
    );

  readonly selectMarketingChecklist = this.marketingChecklistRepo.selectItem;

  onReset() {
    this.marketingChecklistRepo.reset();
  }

  fetchMarketingChecklists = (type: ChecklistType) => {
    return MarketingChecklistAPI.fetchMarketingChecklists(type).pipe(
      this.marketingChecklistRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_checklists_error') }),
    );
  };

  fetchMarketingChecklist = (id: ProjectChecklistId) => {
    return MarketingChecklistAPI.fetchMarketingChecklist(id).pipe(
      this.marketingChecklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_checklists_error') }),
    );
  };

  createMarketingChecklist = (checklist: ProjectChecklistRequest) => {
    return MarketingChecklistAPI.createMarketingChecklist(checklist).pipe(
      this.marketingChecklistRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_marketing_checklist'),
      }),
    );
  };

  updateMarketingChecklist = (id: ProjectChecklistId, checklist: ProjectChecklistRequest) => {
    return MarketingChecklistAPI.updateMarketingChecklist(id, checklist).pipe(
      this.marketingChecklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_marketing_checklist'),
      }),
    );
  };

  duplicateMarketingChecklist = (id: ProjectChecklistId, checklist: CompactProjectChecklistResponse) => {
    return showDeleteConfirmation('Duplicate Checklist', 'Do you really want to duplicate this Checklist?', { btnLabel: 'Duplicate' }).pipe(
      switchMap(() => MarketingChecklistAPI.duplicateMarketingChecklist(id, checklist)),
      this.marketingChecklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_marketing_checklist'),
      }),
    );
  };

  deleteMarketingChecklist = (id: ProjectChecklistId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => MarketingChecklistAPI.deleteMarketingChecklist(id)),
      this.marketingChecklistRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_marketing_checklist'),
      }),
    );
  };

  /* Sections */
  createChecklistSection = (checklistId: ProjectChecklistId, name: string) => {
    return MarketingChecklistAPI.createChecklistSection(checklistId, name).pipe(
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('create_checklist_section'),
      }),
    );
  };

  updateChecklistSection = (checklistId: ProjectChecklistId, id: number, name: string) => {
    return MarketingChecklistAPI.updateChecklistSection(id, name).pipe(
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('update_checklist_section'),
      }),
    );
  };

  deleteChecklistSection = (checklistId: ProjectChecklistId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => MarketingChecklistAPI.deleteChecklistSection(id)),
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('delete_checklist_section'),
      }),
    );
  };

  sortSections = (checklistId: ProjectChecklistId, sectionIds: number[]) => {
    return MarketingChecklistAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const template = this.marketingChecklistRepo.getItem(checklistId)! as ProjectChecklistResponse;
        const newSections = template.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.marketingChecklistRepo.updateOne({
          id: checklistId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
    );
  };

  /* Steps */

  createChecklistSectionStep = (checklistId: ProjectChecklistId, sectionId: number, data: Partial<ProjectChecklistStepResponse>) => {
    return MarketingChecklistAPI.createChecklistStep(sectionId, data).pipe(
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('create_checklist_section_step'),
      }),
    );
  };

  updateChecklistSectionStep = (checklistId: ProjectChecklistId, id: number, data: Partial<ProjectChecklistStepResponse>) => {
    return MarketingChecklistAPI.updateChecklistStep(id, data).pipe(
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('update_checklist_section_step'),
      }),
    );
  };

  deleteChecklistSectionStep = (checklistId: ProjectChecklistId, id: number) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => MarketingChecklistAPI.deleteChecklistStep(id)),
      switchMap(() => this.fetchMarketingChecklist(checklistId)),
      handleMessage({
        type: requestMessage('delete_checklist_section_step'),
      }),
    );
  };

  sortSectionSteps = (checklistId: ProjectChecklistId, sectionId: number, stepIds: number[]) => {
    return MarketingChecklistAPI.sortSectionSteps(sectionId, stepIds).pipe(
      tap(() => {
        const template = this.marketingChecklistRepo.getItem(checklistId)! as ProjectChecklistResponse;
        const affectedSection = R.find(template.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = template.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.marketingChecklistRepo.updateOne({
          id: checklistId,
          changes: () => ({ sections: newSections }),
        });
      }),
    );
  };
}
